import {
  CommonActions,
  NavigationProp,
  ParamListBase,
} from '@react-navigation/native';
import {
  SALES_PORTAL_ROUTES,
  SALES_PORTAL_TAB_ROUTES,
} from '@/navigators/routes';
import { SalesPortalStackParams } from '@/navigators/types';
import { SalsaHostRoute } from '@/generated/salsa-protocol';

export const SALES_PORTAL_PATH = `/sales-portal`;

// microproductParams is an object query param. Without explicit (de)serializers
// react-navigation stringifies it as "[object Object]" and parses it back as
// that literal string, so JSON-encode it instead. parse fails soft: a malformed
// or legacy URL drops the param rather than crashing the linking layer.
const stringifyMicroproductParams = (value: Record<string, string>): string =>
  JSON.stringify(value);
const parseMicroproductParams = (value: string): Record<string, string> => {
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

// Per-tab linking configs. Kept in this file (rather than sales-portal-stack)
// so ROUTE_PARENT_TAB can derive from them without a circular import.
export const accountsTabScreens = {
  [SALES_PORTAL_ROUTES.AccountDetail]: {
    parse: {
      restaurantUUID: (value: string) => value,
      supplierUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/accounts/:supplierUUID/:restaurantUUID`,
  },
  [SALES_PORTAL_ROUTES.AccountList]: {
    path: `${SALES_PORTAL_PATH}/accounts`,
  },
  [SALES_PORTAL_ROUTES.EmployeeList]: {
    parse: {
      restaurantUUID: (value: string) => value,
      supplierUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/accounts/:supplierUUID/:restaurantUUID/employees`,
  },
};

export const orderEntryTabScreens = {
  [SALES_PORTAL_ROUTES.NotFound]: {
    path: `${SALES_PORTAL_PATH}/not-found`,
  },
  [SALES_PORTAL_ROUTES.OrderEntry]: {
    parse: {
      action: (value: string) => value,
      restaurantUUID: (value: string) => value,
      supplierUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/order-entry/:supplierUUID/:restaurantUUID/:action?`,
  },
};

export const insightsTabScreens = {
  [SALES_PORTAL_ROUTES.Insights]: {
    path: `${SALES_PORTAL_PATH}/insights`,
  },
};

export const pricingTabScreens = {
  [SALES_PORTAL_ROUTES.PricingOverview]: {
    path: `${SALES_PORTAL_PATH}/pricing/overview`,
  },
  [SALES_PORTAL_ROUTES.PricingManagement]: {
    path: `${SALES_PORTAL_PATH}/pricing/cost-changes`,
  },
  [SALES_PORTAL_ROUTES.PricingItemManagement]: {
    parse: {
      variantPackUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/pricing/cost-changes/:variantPackUUID`,
  },
  [SALES_PORTAL_ROUTES.PricingManagementReview]: {
    path: `${SALES_PORTAL_PATH}/pricing/cost-changes/review`,
  },
  [SALES_PORTAL_ROUTES.PricingCustomers]: {
    path: `${SALES_PORTAL_PATH}/pricing/customers`,
  },
  [SALES_PORTAL_ROUTES.PricingCustomerDetail]: {
    parse: {
      restaurantUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/pricing/customers/:restaurantUUID`,
  },
  [SALES_PORTAL_ROUTES.PricingAgentReview]: {
    parse: {
      taskTypeUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/pricing/agent/:taskTypeUUID`,
  },
  [SALES_PORTAL_ROUTES.PricingAgentTasks]: {
    path: `${SALES_PORTAL_PATH}/pricing/agent`,
  },
  [SALES_PORTAL_ROUTES.PricingBookmarked]: {
    path: `${SALES_PORTAL_PATH}/pricing/bookmarked`,
  },
  [SALES_PORTAL_ROUTES.PricingMicroProduct]: {
    parse: {
      microproductParams: parseMicroproductParams,
      productId: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/pricing/products/:productId`,
    stringify: {
      microproductParams: stringifyMicroproductParams,
    },
  },
};

export const prospectsTabScreens = {
  [SALES_PORTAL_ROUTES.ProspectDetail]: {
    parse: {
      restaurantUUID: (value: string) => value,
      supplierUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/prospects/:supplierUUID/:restaurantUUID`,
  },
  [SALES_PORTAL_ROUTES.ProspectEmployeeList]: {
    parse: {
      restaurantUUID: (value: string) => value,
      supplierUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/prospects/:supplierUUID/:restaurantUUID/employees`,
  },
  [SALES_PORTAL_ROUTES.ProspectFinder]: {
    parse: { supplierUUID: (value: string) => value },
    path: `${SALES_PORTAL_PATH}/prospects/finder/:supplierUUID`,
  },
  [SALES_PORTAL_ROUTES.Prospects]: {
    path: `${SALES_PORTAL_PATH}/prospects`,
  },
};

export const orderGuidesTabScreens = {
  [SALES_PORTAL_ROUTES.OrderGuides]: {
    parse: {
      orderGuideUUID: (value?: string) => value || undefined,
      restaurantUUID: (value: string) => value,
      supplierUUID: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/order-guides/:supplierUUID/:restaurantUUID/:orderGuideUUID?`,
  },
};

export const directTabScreens = {
  [SALES_PORTAL_ROUTES.Superintelligence]: {
    path: `${SALES_PORTAL_PATH}/superintelligence/:threadId?`,
  },
  [SALES_PORTAL_ROUTES.Copilot]: {
    path: `${SALES_PORTAL_PATH}/copilot`,
  },
  [SALES_PORTAL_ROUTES.Home]: {
    path: SALES_PORTAL_PATH,
  },
  [SALES_PORTAL_ROUTES.PriceQuotesBuilder]: {
    parse: { priceQuoteUUID: (value: string) => value },
    path: `${SALES_PORTAL_PATH}/quotes/:priceQuoteUUID?`,
    stringify: { priceQuoteUUID: (value: string) => value },
  },
  [SALES_PORTAL_ROUTES.Base]: {
    parse: {
      microproductParams: parseMicroproductParams,
      productId: (value: string) => value,
    },
    path: `${SALES_PORTAL_PATH}/:productId`,
    stringify: {
      microproductParams: stringifyMicroproductParams,
    },
  },
};

/**
 * Required params per Sales Portal route. Used by:
 * - Sidebar navigation: picks only these params when switching between tabs
 * - Navigation bridge: validates microproduct → host navigation payloads
 *
 * If a route is added here, both the sidebar and the bridge automatically
 * know which params it needs — no duplication.
 *
 * Route coverage is enforced by tests (every SALES_PORTAL_ROUTES enum value
 * must have an entry). Param name correctness is also validated by tests.
 * Compile-time validation via `satisfies` or `const` type parameters is not
 * possible because Next.js 13 uses Babel which can't parse either syntax.
 */
export const ROUTE_REQUIRED_PARAMS: Record<
  keyof SalesPortalStackParams,
  string[]
> = {
  [SALES_PORTAL_ROUTES.AccountDetail]: ['restaurantUUID', 'supplierUUID'],
  [SALES_PORTAL_ROUTES.AccountList]: [],
  [SALES_PORTAL_ROUTES.Superintelligence]: [],
  [SALES_PORTAL_ROUTES.Base]: ['productId'],
  [SALES_PORTAL_ROUTES.Copilot]: [],
  [SALES_PORTAL_ROUTES.EmployeeList]: ['restaurantUUID', 'supplierUUID'],
  [SALES_PORTAL_ROUTES.Home]: [],
  [SALES_PORTAL_ROUTES.Insights]: [],
  [SALES_PORTAL_ROUTES.NotFound]: [],
  [SALES_PORTAL_ROUTES.OrderEntry]: ['restaurantUUID', 'supplierUUID'],
  [SALES_PORTAL_ROUTES.OrderGuides]: ['restaurantUUID', 'supplierUUID'],
  // priceQuoteUUID is optional, so no required params. See
  // SalesPortalPriceQuotesBuilder in navigators/types.ts.
  [SALES_PORTAL_ROUTES.PriceQuotesBuilder]: [],
  [SALES_PORTAL_ROUTES.PricingAgentReview]: ['taskTypeUUID'],
  [SALES_PORTAL_ROUTES.PricingAgentTasks]: [],
  [SALES_PORTAL_ROUTES.PricingBookmarked]: [],
  [SALES_PORTAL_ROUTES.PricingCustomerDetail]: ['restaurantUUID'],
  [SALES_PORTAL_ROUTES.PricingCustomers]: [],
  [SALES_PORTAL_ROUTES.PricingItemManagement]: ['variantPackUUID'],
  [SALES_PORTAL_ROUTES.PricingManagement]: [],
  [SALES_PORTAL_ROUTES.PricingManagementReview]: [],
  [SALES_PORTAL_ROUTES.PricingMicroProduct]: ['productId'],
  [SALES_PORTAL_ROUTES.PricingOverview]: [],
  [SALES_PORTAL_ROUTES.ProspectDetail]: ['restaurantUUID', 'supplierUUID'],
  [SALES_PORTAL_ROUTES.ProspectEmployeeList]: [
    'restaurantUUID',
    'supplierUUID',
  ],
  [SALES_PORTAL_ROUTES.ProspectFinder]: ['supplierUUID'],
  [SALES_PORTAL_ROUTES.Prospects]: ['supplierUUID'],
};

/**
 * Builds a `screenName -> parentTab` entry list from a tab-screen grouping.
 */
const screensInTab = (
  screens: Record<string, unknown>,
  parentTab: SALES_PORTAL_TAB_ROUTES | null,
): [string, SALES_PORTAL_TAB_ROUTES | null][] =>
  Object.keys(screens).map((screen) => [screen, parentTab]);

/**
 * Maps each Sales Portal stack screen to the tab that contains it (`null` for
 * routes that are direct Tab.Screen entries with no nested stack).
 *
 * Derived from the per-tab screen groupings in `sales-portal-stack.tsx` so
 * adding a screen there automatically updates this map — no second source of
 * truth to keep in sync. Coverage enforced by the matching test in this
 * folder's `__tests__`.
 *
 * Used by the sidebar / microproduct nav handlers to construct nested-navigator
 * payloads — `navigate(tab, { screen, params })` — when targeting a screen
 * inside a lazy-loaded tab. Without this, a flat `navigate(screenName)` fails
 * because the inner stack navigator hasn't mounted yet (the `'NAVIGATE' was
 * not handled by any navigator` warning).
 */
export const ROUTE_PARENT_TAB: Record<
  keyof SalesPortalStackParams,
  SALES_PORTAL_TAB_ROUTES | null
> = Object.fromEntries([
  ...screensInTab(accountsTabScreens, SALES_PORTAL_TAB_ROUTES.Accounts),
  ...screensInTab(insightsTabScreens, SALES_PORTAL_TAB_ROUTES.Insights),
  ...screensInTab(orderEntryTabScreens, SALES_PORTAL_TAB_ROUTES.OrderEntry),
  ...screensInTab(orderGuidesTabScreens, SALES_PORTAL_TAB_ROUTES.OrderGuides),
  ...screensInTab(pricingTabScreens, SALES_PORTAL_TAB_ROUTES.Pricing),
  ...screensInTab(prospectsTabScreens, SALES_PORTAL_TAB_ROUTES.Prospects),
  ...screensInTab(directTabScreens, null),
]) as Record<keyof SalesPortalStackParams, SALES_PORTAL_TAB_ROUTES | null>;

interface NavigateDispatcher {
  dispatch: NavigationProp<ParamListBase>['dispatch'];
}

/**
 * Navigates to a Sales Portal route, automatically wrapping the action in the
 * nested-navigator payload when the target lives inside a tab's stack.
 *
 * Use this instead of `navigation.navigate(SALES_PORTAL_ROUTES.X, params)` for
 * cross-tab navigation: tabs are lazy-mounted, so a flat navigate fails when
 * the target's parent stack hasn't mounted yet (the `'NAVIGATE' was not
 * handled by any navigator` warning).
 *
 * Two signatures:
 * - Static route: `route` and `params` are correlated, so the param shape is
 *   checked against the specific route.
 * - Dynamic route (`keyof SalesPortalStackParams`): for callers that only know
 *   the route at runtime (e.g. sidebar items built from config).
 */
export function navigateToSalesPortalRoute<
  R extends keyof SalesPortalStackParams,
>(
  navigation: NavigateDispatcher,
  route: R,
  params?: SalesPortalStackParams[R],
): void;
export function navigateToSalesPortalRoute(
  navigation: NavigateDispatcher,
  route: keyof SalesPortalStackParams,
  params?: SalesPortalStackParams[keyof SalesPortalStackParams],
): void;
export function navigateToSalesPortalRoute(
  navigation: NavigateDispatcher,
  route: keyof SalesPortalStackParams,
  params?: SalesPortalStackParams[keyof SalesPortalStackParams],
): void {
  const parentTab = ROUTE_PARENT_TAB[route];
  if (parentTab) {
    navigation.dispatch(
      CommonActions.navigate({
        name: parentTab as string,
        params: { params, screen: route as string },
      }),
    );
  } else {
    navigation.dispatch(
      CommonActions.navigate({ name: route as string, params }),
    );
  }
}

/**
 * Maps salsa's friendly route aliases to internal React Navigation route names.
 * Microproducts use `SalsaHostRoute.ORDER_ENTRY` — they never see internal
 * names like `SALES_PORTAL_ROUTES.OrderEntry`.
 */
export const HOST_ROUTE_MAP: Record<string, keyof SalesPortalStackParams> = {
  [SalsaHostRoute.ORDER_ENTRY]: SALES_PORTAL_ROUTES.OrderEntry,
};

/** Routes that RA advertises as navigable from microproducts. */
export const SUPPORTED_HOST_ROUTES: string[] = Object.keys(HOST_ROUTE_MAP);
